import axios from "axios";
import React, { useContext, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import LoginReg from "../LoginReg";
import { AuthContext, base_url } from "../../context/Authcontext";
import { useNavigate } from "react-router-dom";
// Only the carousel plugin, not the whole bundle again: index.js already
// imports bootstrap.bundle, but webpack does not expose it as a global, so
// the data-bs-ride attribute alone never starts anything.
import BsCarousel from "bootstrap/js/dist/carousel";
import { useBanners } from "../../utils/useBanners";

// Shown when the banner API returns nothing, so the hero never collapses to
// an error string or a zero-height gap. These are the local 1600x400
// creatives; once banners exist in the database they take over automatically.
const FALLBACK_BANNERS = [
  "/assets/images/hero-1.jpg",
  "/assets/images/hero-2.jpg",
  "/assets/images/hero-3.jpg",
  "/assets/images/hero-4.jpg",
  "/assets/images/hero-5.jpg",
  "/assets/images/hero-6.jpg",
  "/assets/images/hero-7.jpg",
  "/assets/images/hero-8.jpg",
];

const Carousel = () => {
  // Hero banners only, already filtered to what is live right now and to
  // this device. null while the request is in flight.
  const banners = useBanners("hero");
  const isLoading = banners === null;
  const [active, setactive] = useState(false);
  const { isAuthenticated } = useContext(AuthContext);
  const [regactive, setregactive] = useState(false);
  const carouselRef = useRef(null);
  const navigate = useNavigate();
  // The hero takes the shape of the artwork itself (width / height of the
  // first slide), so a banner is shown whole at every screen size instead
  // of being cropped into a fixed slot per breakpoint. null until the
  // first image has loaded; CSS falls back to 4:1 meanwhile.
  const [heroRatio, setHeroRatio] = useState(null);

  // Bootstrap's JS and CSS are already imported globally in src/index.js.
  // This component used to inject both from a CDN as well, which added two
  // render-blocking third-party requests for code the bundle already had.
  //
  // It also fetched the whole SPB game list on every home load to find an
  // "aviator" id it never used -- one of the three duplicate particularGame
  // requests in #10.

  // Open Game
  const openGameById = async (gameId) => {
    if (!gameId) return;

    try {
      const token = localStorage.getItem("game_token");

      const response = await axios.post(
        base_url + "/api/gamelaunch/url",
        { token, gameId }
      );

      if (response?.data?.success) {
        window.location.href = response.data.url;
      }
    } catch (err) {
      console.log(err);
    }
  };

  /* Each slide can carry its own destination now. Every slide used to
     open the same hardcoded game whatever the artwork advertised, because
     there was nowhere to record where a banner should lead. */
  const handleSlideClick = (slide) => {
    if (slide && slide.link) {
      const link = slide.link;
      if (/^https?:\/\//i.test(link)) window.open(link, "_blank", "noopener");
      else navigate(link);
      return;
    }
    if (isAuthenticated) {
      openGameById("JIL-chickendash");
    } else {
      toast.error("Please Login to Play.");
      setactive(true);
    }
  };

  // Banners whose image failed to load. They are taken out of the rotation:
  // hiding just the image (as before) still left their slide in the
  // carousel, so it slid in as an empty frame between two real banners.
  const [failed, setFailed] = useState(() => new Set());
  const dropSlide = (key) =>
    setFailed((prev) => (prev.has(key) ? prev : new Set(prev).add(key)));

  const fallbackSlides = FALLBACK_BANNERS.map((src, i) => ({
    key: `fallback-${i}`,
    src,
    alt: `Promotion ${i + 1}`,
    link: null,
    wide: true, // 1600x400
  }));

  const liveSlides = (banners && banners.length
    ? banners.map((b) => ({
        key: b.id,
        src: b.src,
        alt: b.title || "Banner",
        link: b.link,
        wide: b.wide,
      }))
    : fallbackSlides
  ).filter((s) => !failed.has(s.key));

  // Every uploaded banner broken: show the bundled art rather than nothing.
  const slides = liveSlides.length
    ? liveSlides
    : fallbackSlides.filter((s) => !failed.has(s.key));

  const hasMultiple = slides.length > 1;

  // React renders this after DOMContentLoaded, so Bootstrap's auto-init has
  // already run and will never see it. Start it by hand instead.
  useEffect(() => {
    const el = carouselRef.current;
    if (!el || slides.length < 2) return undefined;

    const instance = BsCarousel.getOrCreateInstance(el, {
      interval: 4000,
      ride: "carousel",
      pause: "hover",
      wrap: true,
    });
    instance.cycle();

    return () => instance.dispose();
  }, [slides.map((s) => s.key).join("|")]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reserve the hero's space while loading so the rest of the page does not
  // jump down when the banners arrive.
  if (isLoading) {
    return (
      <div className="container-fluid p-0">
        <div id="mainCarousel" className="rb-banner-skeleton rb-skeleton" />
      </div>
    );
  }

  return (
    <>
      <LoginReg
        active={active}
        setactive={setactive}
        regactive={regactive}
        setregactive={setregactive}
      />

      <div className="container-fluid p-0">
        <div
          /* Remount when a broken banner drops out, so Bootstrap starts
             clean instead of animating a list React just changed. */
          key={slides.map((s) => s.key).join("|")}
          ref={carouselRef}
          id="mainCarousel"
          className="carousel slide rb-hero--fit"
          style={heroRatio ? { "--rb-hero-ratio": String(heroRatio) } : undefined}
          data-bs-ride="carousel"
          data-bs-interval="4000"
        >
          {hasMultiple && (
            <div className="carousel-indicators">
              {slides.map((slide, index) => (
                <button
                  key={slide.key}
                  type="button"
                  data-bs-target="#mainCarousel"
                  data-bs-slide-to={index}
                  className={index === 0 ? "active" : ""}
                  aria-current={index === 0 ? "true" : undefined}
                  aria-label={`Slide ${index + 1}`}
                />
              ))}
            </div>
          )}

          <div className="carousel-inner">
            {slides.map((slide, index) => (
              <div
                key={slide.key}
                className={index === 0 ? "carousel-item active" : "carousel-item"}
              >
                <img
                  src={slide.src}
                  className={
                    slide.wide
                      ? "img-fluid d-block w-100 rb-banner__img rb-banner__img--wide"
                      : "img-fluid d-block w-100 rb-banner__img"
                  }
                  alt={slide.alt}
                  onClick={() => handleSlideClick(slide)}
                  style={{ cursor: "pointer" }}
                  /* All slides load up front; the first one first. They used
                     to be loading="lazy" -- but an inactive carousel slide
                     is display:none, so a lazy image never started until
                     its slide was already moving in, and the carousel
                     showed an empty frame between banners. */
                  loading="eager"
                  fetchpriority={index === 0 ? "high" : "low"}
                  decoding="async"
                  onLoad={
                    index === 0
                      ? (e) => {
                          const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
                          if (w && h) setHeroRatio(w / h);
                        }
                      : undefined
                  }
                  /* Any upload is cropped to the slot rather than setting
                     the slot's height, so an odd-sized banner cannot push
                     the page around. */
                  onError={() => dropSlide(slide.key)}
                />
              </div>
            ))}
          </div>

          {hasMultiple && (
            <>
              <button
                className="carousel-control-prev"
                type="button"
                data-bs-target="#mainCarousel"
                data-bs-slide="prev"
              >
                <span className="carousel-control-prev-icon" aria-hidden="true" />
                <span className="visually-hidden">Previous</span>
              </button>
              <button
                className="carousel-control-next"
                type="button"
                data-bs-target="#mainCarousel"
                data-bs-slide="next"
              >
                <span className="carousel-control-next-icon" aria-hidden="true" />
                <span className="visually-hidden">Next</span>
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Carousel;
